import React, { useState } from 'react'
import {
  LinkAuthenticationElement,
  PaymentElement,
  useElements,
} from '@stripe/react-stripe-js'
import { Stripe, StripePaymentElementChangeEvent } from '@stripe/stripe-js'

import LoadingOverlay from 'shared/genericComponents/LoadingOverlay/LoadingOverlay'
import { getIcon } from 'shared/genericComponents/Icon/Icon'
import { getButton } from 'shared/genericComponents/Button/Button'
import { Modal, ModalType } from 'shared/genericComponents/Modal/Modal'

import { useCurrentUser } from 'shared/lib/hooks'
import currencyFormatter from 'shared/utils/currencyFormatter'
import { STRIPE_PAYMENT_METHODS } from 'shared/constants'

import * as styles from './CheckoutForm.m.css'

const ErrorIcon = getIcon(styles.errorIcon)
const SubmitButton = getButton([styles.payButton])

type Props = {
  stripe: Stripe
  amount: number
  name?: string
  email?: string
  onReady: () => void
  paymentMethodChangeCallback?: (method: string) => void
}

export const CheckoutForm: React.FC<Props> = (props) => {
  const { stripe, amount, name, email, onReady, paymentMethodChangeCallback } =
    props

  const elements = useElements()
  const { data: user, isLoading: isUserLoading } = useCurrentUser()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [formComplete, setFormComplete] = useState(false)

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = async (
    e,
  ) => {
    e.preventDefault()

    if (!stripe || !elements || !formComplete) {
      // Stripe.js has not yet loaded or form in incomplete.
      return
    }

    setIsLoading(true)
    setShowModal(false)

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment-complete`,
        payment_method_data: {
          billing_details: {
            name: name ?? user.name,
            email: email ?? user.email,
          },
        },
      },
    })

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, the customer will be redirected to
    // `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment,
    // then redirected to the `return_url`.
    if (error.type === 'card_error' || error.type === 'validation_error') {
      setError(error.message)
    } else if (
      error.code === 'payment_intent_unexpected_state' &&
      error.payment_intent.status === 'succeeded'
    ) {
      setError('This payment was already completed.')
    } else {
      setError('An unexpected error occured.')
    }

    setIsLoading(false)
  }

  const handlePayButtonClick: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()

    if (formComplete) {
      setShowModal(true)
    }

    return false
  }

  const handleCancel = () => {
    setShowModal(false)
  }

  const change = (e: StripePaymentElementChangeEvent) => {
    setFormComplete(e.complete)
    paymentMethodChangeCallback(e.value.type)
  }

  const onStripeLoadError = () => {
    setError('Error loading payment form. Please try again later.')
    onReady()
  }

  return (
    <>
      {isLoading && <LoadingOverlay>Processing payment...</LoadingOverlay>}
      {!isUserLoading ? (
        <form id="payment-form" onSubmit={handlePayButtonClick}>
          <LinkAuthenticationElement
            id="link-authentication-element"
            options={{ defaultValues: { email: email ?? user.email } }}
            onLoadError={onStripeLoadError}
          />
          <PaymentElement
            id="payment-element"
            onChange={change}
            onReady={onReady}
            onLoadError={onStripeLoadError}
            options={{
              fields: {
                billingDetails: {
                  email: 'never',
                },
              },
              paymentMethodOrder: [
                STRIPE_PAYMENT_METHODS.ACH,
                STRIPE_PAYMENT_METHODS.CARD,
              ],
            }}
          />
          <div className={styles.buttonContainer}>
            <SubmitButton
              type="submit"
              disabled={
                isLoading || !stripe || !elements || !formComplete || isLoading
              }
            >
              Pay
            </SubmitButton>
          </div>
          <div className={styles.linkMessage}>
            Saved payment information is handled by Link from Stripe.{' '}
            <a href="https://app.link.com/login" target="_blank">
              Click Here
            </a>{' '}
            to visit Link in order to manage your payment methods.
          </div>
          {error && (
            <div className={styles.errorMessage}>
              <ErrorIcon icon="error" />
              {error}
            </div>
          )}
          {showModal && (
            <Modal
              title="Confirm Payment"
              type={ModalType.Confirm}
              okCallback={handleSubmit}
              cancelCallback={handleCancel}
              showCloseX={true}
            >
              <p>
                You will be charged{' '}
                <strong>{currencyFormatter.format(amount)}</strong> to the
                selected payment method.
              </p>
              <p>Are you sure you want to continue?</p>
            </Modal>
          )}
        </form>
      ) : null}
    </>
  )
}

export default CheckoutForm
